// Kiosco del comensal (público por token, sin login) — patrón andón/reparto.
// El comensal escanea/teclea su badge y elige entre las opciones de los próximos
// servicios. Escribe vía autenticacion.kiosco_elegir. Ruta /kiosco/:token.
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UtensilsCrossed, Loader2, Check, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { LOCALE } from '@/lib/utils/regional';

interface Opcion { grupo: string; receta_id: string; receta: string; es_default: boolean; elegida: boolean }
interface DiaServicio { fecha: string; servicio: string; opciones: Opcion[] }
interface Resp {
  valido: boolean; motivo?: string; kiosco?: string;
  comensal?: { id: string; nombre: string; codigo: string };
  dias?: DiaServicio[];
}
const SRV: Record<string, string> = { desayuno: 'Desayuno', almuerzo_1: 'Almuerzo', almuerzo_2: 'Almuerzo 2', merienda: 'Merienda', cena: 'Cena', break: 'Break', dieta: 'Dieta' };
const fmt = (iso: string): string => new Date(iso + 'T12:00').toLocaleDateString(LOCALE, { weekday: 'long', day: 'numeric', month: 'long' });

export function Kiosco() {
  const { token = '' } = useParams();
  const qc = useQueryClient();
  const [codigoInput, setCodigoInput] = useState('');
  const [codigo, setCodigo] = useState('');

  const { data, isFetching } = useQuery<Resp>({
    queryKey: ['kiosco', token, codigo],
    queryFn: async () => (await apiClient.post<Resp>('/rpc/kiosco_comensal', { _token: token, _codigo: codigo, _dias: 3 })).data,
    enabled: !!codigo,
  });

  const elegir = useMutation({
    mutationFn: (p: { fecha: string; servicio: string; grupo: string; receta_id: string | null }) =>
      apiClient.post('/rpc/kiosco_elegir', { _token: token, _codigo: codigo, _fecha: p.fecha, _servicio: p.servicio, _grupo: p.grupo, _receta_id: p.receta_id }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['kiosco', token, codigo] }); toast.success('Elección guardada'); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const salir = () => { setCodigo(''); setCodigoInput(''); };

  // Pantalla de identificación
  if (!codigo || (data && !data.valido)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-slate-900 p-6 text-white">
        <UtensilsCrossed className="h-16 w-16 text-emerald-400" />
        <h1 className="text-2xl font-semibold">Kiosco del comedor</h1>
        <p className="text-slate-300">Escanea o teclea tu código de comensal</p>
        <form onSubmit={(e) => { e.preventDefault(); setCodigo(codigoInput.trim()); }} className="flex w-full max-w-xs flex-col gap-3">
          <input autoFocus className="rounded-lg border border-slate-600 bg-slate-800 px-4 py-3 text-center text-xl tracking-widest text-white placeholder:text-slate-500"
            placeholder="B001" value={codigoInput} onChange={(e) => setCodigoInput(e.target.value)} />
          <button type="submit" className="rounded-lg bg-emerald-500 px-4 py-3 text-lg font-semibold text-white hover:bg-emerald-600">Entrar</button>
        </form>
        {data && !data.valido && <p className="text-red-400">{data.motivo === 'comensal' ? 'Código no reconocido' : 'Kiosco no válido'}. Intenta de nuevo.</p>}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 dark:bg-slate-900">
      <div className="mx-auto max-w-2xl space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{data?.kiosco}</p>
            <h1 className="text-xl font-semibold">Hola, {data?.comensal?.nombre}</h1>
          </div>
          <button onClick={salir} className="flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm"><LogOut className="h-4 w-4" /> Salir</button>
        </div>

        {isFetching && <p className="text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin" /> Cargando…</p>}

        {data?.dias?.length ? data.dias.map((ds) => (
          <div key={`${ds.fecha}-${ds.servicio}`} className="rounded-xl border bg-white p-4 shadow-sm dark:bg-slate-800">
            <p className="text-xs uppercase text-muted-foreground">{fmt(ds.fecha)}</p>
            <p className="mb-2 font-semibold">{SRV[ds.servicio] ?? ds.servicio}</p>
            {/* opciones agrupadas por grupo */}
            {Array.from(new Set(ds.opciones.map((o) => o.grupo))).map((g) => (
              <div key={g} className="mb-2">
                <p className="text-[11px] uppercase text-muted-foreground">{g}</p>
                <div className="flex flex-wrap gap-2">
                  {ds.opciones.filter((o) => o.grupo === g).map((o) => (
                    <button key={o.receta_id} disabled={elegir.isPending}
                      onClick={() => elegir.mutate({ fecha: ds.fecha, servicio: ds.servicio, grupo: g, receta_id: o.elegida ? null : o.receta_id })}
                      className={`flex items-center gap-2 rounded-lg border-2 px-3 py-2 text-sm transition ${o.elegida ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'border-slate-200 hover:border-emerald-400 dark:border-slate-600'}`}>
                      {o.elegida && <Check className="h-4 w-4 text-emerald-600" />}
                      {o.receta.replace('Plato fuerte — ', '')}
                      {o.es_default && !o.elegida && <span className="text-[10px] text-muted-foreground">(por defecto)</span>}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )) : !isFetching && <p className="rounded-xl border bg-white p-6 text-center text-muted-foreground dark:bg-slate-800">No hay opciones para elegir en los próximos días. ¡Buen provecho!</p>}
      </div>
    </div>
  );
}
